import { defineStore } from 'pinia'
import type { SeoUrl } from '~/lib/utils/seo'
import type { components } from '~/types/shopware'

type EntityType = 'product' | 'category'

type Id = string
type LangId = string

// Internal key builder to cache per-language results
const cacheKey = (type: EntityType, id: Id, lang: LangId) => `${type}:${lang}:${id}`

export const useSeoUrlStore = defineStore('seoUrlStore', () => {
  const { apiClient } = useShopwareContext()
  const { getCurrentShopLanguage } = useShopLanguages()

  // Reactive caches (returned so Pinia can serialize for SSR hydration)
  const map = reactive<Record<string, string>>({})

  // Coalesce concurrent requests per language to avoid duplicate network calls
  const pending: Record<EntityType, Record<LangId, Promise<void> | null>> = reactive({
    product: {},
    category: {},
  })

  const ensureProductUrls = async (ids: string[], languageId?: string) => {
    const currentLang = getCurrentShopLanguage()
    const lang = languageId ?? currentLang ?? 'default'
    const awaitPending = pending.product[lang]
    if (awaitPending) await awaitPending
    const missing = ids.filter((id) => !map[cacheKey('product', id, lang)])
    if (!missing.length) return

    try {
      type Filter = components['schemas']['Filters'][number]
      const filters: Filter[] = [
        { type: 'equalsAny', field: 'foreignKey', value: missing.join('|') },
        { type: 'equals', field: 'routeName', value: 'frontend.detail.page' },
        { type: 'equals', field: 'isCanonical', value: true },
      ]
      if (currentLang) filters.push({ type: 'equals', field: 'languageId', value: currentLang })

      pending.product[lang] = apiClient
        .invoke('readSeoUrl post /seo-url', { body: { filter: filters } })
        .then((response) => {
          ;(response.data?.elements || []).forEach((el: SeoUrl) => {
            const url = el?.seoPathInfo ? `/${el.seoPathInfo}` : `${el?.pathInfo || ''}`
            if (url) map[cacheKey('product', el.foreignKey, lang)] = url
          })
        })
        .finally(() => {
          pending.product[lang] = null
        })

      await pending.product[lang]
    } catch (err) {
      console.warn('[seoUrlStore] Failed to resolve product SEO URLs', err)
    }
  }

  const ensureCategoryUrls = async (ids: string[], languageId?: string) => {
    const currentLang = getCurrentShopLanguage()
    const lang = languageId ?? currentLang ?? 'default'
    const awaitPending = pending.category[lang]
    if (awaitPending) await awaitPending
    const missing = ids.filter((id) => !map[cacheKey('category', id, lang)])
    if (!missing.length) return

    try {
      type Filter = components['schemas']['Filters'][number]
      const filters: Filter[] = [
        { type: 'equalsAny', field: 'foreignKey', value: missing.join('|') },
        { type: 'equals', field: 'routeName', value: 'frontend.navigation.page' },
        { type: 'equals', field: 'isCanonical', value: true },
      ]
      if (currentLang) filters.push({ type: 'equals', field: 'languageId', value: currentLang })

      pending.category[lang] = apiClient
        .invoke('readSeoUrl post /seo-url', { body: { filter: filters } })
        .then((response) => {
          ;(response.data?.elements || []).forEach((el: SeoUrl) => {
            const url = el?.seoPathInfo ? `/${el.seoPathInfo}` : `${el?.pathInfo || ''}`
            if (url) map[cacheKey('category', el.foreignKey, lang)] = url
          })
        })
        .finally(() => {
          pending.category[lang] = null
        })

      await pending.category[lang]
    } catch (err) {
      console.warn('[seoUrlStore] Failed to resolve category SEO URLs', err)
    }
  }

  const getProductUrlById = (id: string, languageId?: string): string | undefined => {
    const lang = languageId ?? getCurrentShopLanguage() ?? 'default'
    return map[cacheKey('product', id, lang)]
  }

  const getCategoryUrlById = (id: string, languageId?: string): string | undefined => {
    const lang = languageId ?? getCurrentShopLanguage() ?? 'default'
    return map[cacheKey('category', id, lang)]
  }

  return {
    // expose cache so SSR can serialize and client can hydrate
    map,
    ensureProductUrls,
    ensureCategoryUrls,
    getProductUrlById,
    getCategoryUrlById,
    clearAll: () => {
      for (const key in map) delete map[key]
    },
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSeoUrlStore, import.meta.hot))
}
